/**
 * Customer module tour demos
 * Demo HTML templates specific to customer and lead modules
 */

import type { TourDemoType } from '@/utils/tour-styles/types'

/**
 * Generate customer-specific demo HTML
 * Returns empty string if demo is not customer/lead-related
 */
export const getCustomerDemoHTML = (demo?: TourDemoType): string => {
    if (!demo) return ''

    switch (demo) {
        case 'click-create-button':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 180px;">
                <div style="width: 280px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Header with button -->
                    <div style="padding: 10px 12px; display: flex; justify-content: space-between; align-items: center; border-bottom: 1px solid #f1f5f9;">
                        <div style="display: flex; align-items: center; gap: 6px;">
                            <div style="width: 60px; height: 8px; background: #e5e7eb; border-radius: 3px;"></div>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M9 18l6-6-6-6"/></svg>
                            <div style="width: 80px; height: 8px; background: #3b82f6; border-radius: 3px;"></div>
                        </div>
                        <!-- Create button with pulse -->
                        <div style="position: relative;">
                            <div style="position: absolute; inset: -3px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); border-radius: 6px; animation: tour-btn-pulse 1.5s ease-in-out infinite; opacity: 0.4;"></div>
                            <button style="position: relative; display: flex; align-items: center; gap: 4px; padding: 6px 12px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); color: white; border: none; border-radius: 4px; font-size: 10px; font-weight: 500;">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M12 5v14M5 12h14"/></svg>
                                Tạo mới
                            </button>
                        </div>
                    </div>
                    <!-- Table skeleton -->
                    <div style="padding: 0;">
                        <!-- Table header -->
                        <div style="display: grid; grid-template-columns: 20px 40px 80px 70px 50px; gap: 8px; padding: 8px 10px; background: #f8fafc; border-bottom: 1px solid #e5e7eb;">
                            <div style="width: 12px; height: 12px; border: 1.5px solid #d1d5db; border-radius: 2px;"></div>
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                        </div>
                        <!-- Table rows -->
                        <div style="display: grid; grid-template-columns: 20px 40px 80px 70px 50px; gap: 8px; padding: 8px 10px; border-bottom: 1px solid #f3f4f6;">
                            <div style="width: 12px; height: 12px; border: 1.5px solid #e5e7eb; border-radius: 2px;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.1s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.2s;"></div>
                            <div style="height: 6px; background: #bbf7d0; border-radius: 2px;"></div>
                        </div>
                        <div style="display: grid; grid-template-columns: 20px 40px 80px 70px 50px; gap: 8px; padding: 8px 10px; border-bottom: 1px solid #f3f4f6;">
                            <div style="width: 12px; height: 12px; border: 1.5px solid #e5e7eb; border-radius: 2px;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.15s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.25s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.35s;"></div>
                            <div style="height: 6px; background: #fecaca; border-radius: 2px;"></div>
                        </div>
                        <div style="display: grid; grid-template-columns: 20px 40px 80px 70px 50px; gap: 8px; padding: 8px 10px;">
                            <div style="width: 12px; height: 12px; border: 1.5px solid #e5e7eb; border-radius: 2px;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.2s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.3s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.4s;"></div>
                            <div style="height: 6px; background: #fef08a; border-radius: 2px;"></div>
                        </div>
                    </div>
                </div>
                <!-- Cursor pointing to button -->
                <div style="display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 10px; animation: tour-cursor-bounce 1s ease-in-out infinite;">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#374151" stroke="#374151" stroke-width="1">
                        <path d="M4 4l7.07 17 2.51-7.39L21 11.07z"/>
                    </svg>
                    <span style="font-size: 10px; color: #6b7280; font-weight: 500;">Click vào nút "Tạo mới"</span>
                </div>
                <style>
                    @keyframes tour-btn-pulse {
                        0%, 100% { transform: scale(1); opacity: 0.3; }
                        50% { transform: scale(1.15); opacity: 0.5; }
                    }
                    @keyframes tour-cursor-bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-3px); }
                    }
                    @keyframes tour-skeleton-shimmer {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                </style>
            </div>
        `
        case 'sidebar-click-customer':
            return `
            <div class="tour-demo" style="padding: 12px;">
                <div style="width: 200px; background: white; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Menu items -->
                    <div style="padding: 8px 12px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #f1f5f9;">
                        <div style="width: 20px; height: 20px; background: #e5e7eb; border-radius: 4px;"></div>
                        <span style="font-size: 11px; color: #6b7280;">Dashboard</span>
                    </div>
                    <div style="padding: 8px 12px; display: flex; align-items: center; gap: 10px; border-bottom: 1px solid #f1f5f9;">
                        <div style="width: 20px; height: 20px; background: #e5e7eb; border-radius: 4px;"></div>
                        <span style="font-size: 11px; color: #6b7280;">Marketing</span>
                    </div>
                    <!-- Khách hàng - highlighted -->
                    <div style="padding: 8px 12px; display: flex; align-items: center; gap: 10px; background: #eff6ff; border-left: 3px solid #3b82f6; animation: tour-plus-pulse 1.5s ease-in-out infinite; cursor: pointer;">
                        <div style="width: 20px; height: 20px; background: #dbeafe; border-radius: 4px; display: flex; align-items: center; justify-content: center;">
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
                        </div>
                        <span style="font-size: 11px; color: #3b82f6; font-weight: 500;">Khách hàng</span>
                    </div>
                    <div style="padding: 8px 12px; display: flex; align-items: center; gap: 10px;">
                        <div style="width: 20px; height: 20px; background: #e5e7eb; border-radius: 4px;"></div>
                        <span style="font-size: 11px; color: #6b7280;">Resellers</span>
                    </div>
                </div>
            </div>
        `
        case 'customer-create-options':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 200px;">
                <div style="width: 300px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Header with buttons -->
                    <div style="padding: 10px 12px; display: flex; justify-content: flex-end; align-items: center; gap: 8px; border-bottom: 1px solid #f1f5f9;">
                        <!-- Template button -->
                        <button style="display: flex; align-items: center; gap: 4px; padding: 6px 10px; background: white; color: #374151; border: 1px solid #d1d5db; border-radius: 4px; font-size: 9px; font-weight: 500;">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/></svg>
                            Mẫu Template
                        </button>
                        <!-- Import button -->
                        <button style="display: flex; align-items: center; gap: 4px; padding: 6px 10px; background: white; color: #374151; border: 1px solid #d1d5db; border-radius: 4px; font-size: 9px; font-weight: 500;">
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12"/></svg>
                            Import
                        </button>
                        <!-- Create button with pulse -->
                        <div style="position: relative;">
                            <div style="position: absolute; inset: -3px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); border-radius: 6px; animation: tour-btn-pulse 1.5s ease-in-out infinite; opacity: 0.4;"></div>
                            <button style="position: relative; display: flex; align-items: center; gap: 4px; padding: 6px 10px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); color: white; border: none; border-radius: 4px; font-size: 9px; font-weight: 500;">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3"><path d="M12 5v14M5 12h14"/></svg>
                                Tạo KH
                            </button>
                        </div>
                    </div>
                    <!-- Table skeleton -->
                    <div style="padding: 0;">
                        <div style="display: grid; grid-template-columns: 80px 70px 50px 60px; gap: 6px; padding: 8px 10px; background: #f8fafc; border-bottom: 1px solid #e5e7eb;">
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                            <div style="height: 8px; background: #d1d5db; border-radius: 2px;"></div>
                        </div>
                        <div style="display: grid; grid-template-columns: 80px 70px 50px 60px; gap: 6px; padding: 8px 10px; border-bottom: 1px solid #f3f4f6;">
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.1s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.2s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                        </div>
                        <div style="display: grid; grid-template-columns: 80px 70px 50px 60px; gap: 6px; padding: 8px 10px;">
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.15s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.25s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px; animation: tour-skeleton-shimmer 1.5s ease-in-out infinite 0.35s;"></div>
                            <div style="height: 6px; background: #e5e7eb; border-radius: 2px;"></div>
                        </div>
                    </div>
                    <!-- Info text -->
                    <div style="padding: 8px 12px; background: #f0fdf4; border-top: 1px solid #bbf7d0;">
                        <div style="font-size: 9px; color: #166534; line-height: 1.4;">
                            <strong>2 cách tạo khách hàng:</strong><br/>
                            1. Click "Tạo KH" để nhập form<br/>
                            2. Tải "Mẫu Template" → điền → "Import"
                        </div>
                    </div>
                </div>
                <style>
                    @keyframes tour-btn-pulse {
                        0%, 100% { transform: scale(1); opacity: 0.3; }
                        50% { transform: scale(1.15); opacity: 0.5; }
                    }
                    @keyframes tour-skeleton-shimmer {
                        0%, 100% { opacity: 1; }
                        50% { opacity: 0.5; }
                    }
                </style>
            </div>
        `
        case 'customer-select-row':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 180px;">
                <div style="width: 320px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Table header -->
                    <div style="display: grid; grid-template-columns: 120px 80px 80px; gap: 8px; padding: 10px 12px; background: #f8fafc; border-bottom: 1px solid #e5e7eb;">
                        <div style="font-size: 9px; color: #6b7280; font-weight: 600;">Tên Khách Hàng</div>
                        <div style="font-size: 9px; color: #6b7280; font-weight: 600;">Lĩnh Vực</div>
                        <div style="font-size: 9px; color: #6b7280; font-weight: 600;">Email</div>
                    </div>
                    <!-- Row 1 - highlighted -->
                    <div style="position: relative;">
                        <div style="position: absolute; inset: 0; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); opacity: 0.1; animation: tour-row-pulse 1.5s ease-in-out infinite;"></div>
                        <div style="display: grid; grid-template-columns: 120px 80px 80px; gap: 8px; padding: 10px 12px; border-bottom: 1px solid #f3f4f6; cursor: pointer; position: relative;">
                            <div style="font-size: 10px; color: #1A237E; font-weight: 500; display: flex; align-items: center; gap: 4px;">
                                <span style="color: #6b7280;">›</span> 15491 - DEVELOPER
                            </div>
                            <div style="font-size: 9px; color: #6b7280;">Retail</div>
                            <div style="font-size: 9px; color: #9ca3af;">dev***@vn...</div>
                        </div>
                    </div>
                    <!-- Row 2 -->
                    <div style="display: grid; grid-template-columns: 120px 80px 80px; gap: 8px; padding: 10px 12px; border-bottom: 1px solid #f3f4f6;">
                        <div style="font-size: 10px; color: #374151;">15490 - DEV (3)</div>
                        <div style="font-size: 9px; color: #6b7280;">Retail</div>
                        <div style="font-size: 9px; color: #9ca3af;">dev***@vn...</div>
                    </div>
                    <!-- Info -->
                    <div style="padding: 8px 12px; background: #eff6ff; border-top: 1px solid #bfdbfe;">
                        <div style="font-size: 9px; color: #1e40af;">Click vào tên khách hàng để xem chi tiết</div>
                    </div>
                </div>
                <style>
                    @keyframes tour-row-pulse {
                        0%, 100% { opacity: 0.05; }
                        50% { opacity: 0.15; }
                    }
                </style>
            </div>
        `
        case 'customer-detail-tabs':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 120px;">
                <div style="width: 340px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Breadcrumb -->
                    <div style="padding: 8px 12px; background: #f8fafc; border-bottom: 1px solid #e5e7eb;">
                        <div style="font-size: 9px; color: #6b7280;">Dashboard › Quản Lý Khách Hàng › <span style="color: #1A237E;">15491 - DEVELOPER</span></div>
                    </div>
                    <!-- Tabs -->
                    <div style="display: flex; gap: 4px; padding: 8px 12px; border-bottom: 1px solid #e5e7eb; overflow-x: auto;">
                        <div style="padding: 6px 10px; font-size: 9px; color: #6b7280; white-space: nowrap;">THÔNG TIN</div>
                        <div style="position: relative; padding: 6px 10px; font-size: 9px; color: #1A237E; font-weight: 600; white-space: nowrap; border-radius: 4px; background: #e8eaf6;">
                            <div style="position: absolute; inset: -2px; border: 2px solid #1A237E; border-radius: 6px; animation: tour-tab-pulse 1.5s ease-in-out infinite;"></div>
                            QUÁ TRÌNH BÁN HÀNG
                        </div>
                        <div style="padding: 6px 10px; font-size: 9px; color: #6b7280; white-space: nowrap;">HỢP ĐỒNG</div>
                        <div style="padding: 6px 10px; font-size: 9px; color: #6b7280; white-space: nowrap;">DỊCH VỤ</div>
                    </div>
                    <!-- Info -->
                    <div style="padding: 8px 12px; background: #eff6ff;">
                        <div style="font-size: 9px; color: #1e40af;">Click tab "QUÁ TRÌNH BÁN HÀNG" để quản lý hành trình bán hàng</div>
                    </div>
                </div>
                <style>
                    @keyframes tour-tab-pulse {
                        0%, 100% { opacity: 0.3; transform: scale(1); }
                        50% { opacity: 0.8; transform: scale(1.02); }
                    }
                </style>
            </div>
        `
        case 'contract-create-button':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 200px;">
                <div style="width: 340px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Header -->
                    <div style="padding: 10px 12px; background: #f8fafc; border-bottom: 1px solid #e5e7eb;">
                        <div style="font-size: 10px; color: #374151; font-weight: 600;">Quá Trình Bán Hàng #1</div>
                    </div>
                    <!-- Sales Pipeline stages -->
                    <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; padding: 10px; border-bottom: 1px solid #e5e7eb;">
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #22c55e; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                            </div>
                            <div style="font-size: 7px; color: #6b7280;">Trao đổi</div>
                        </div>
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #22c55e; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                            </div>
                            <div style="font-size: 7px; color: #6b7280;">Meeting</div>
                        </div>
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #22c55e; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                            </div>
                            <div style="font-size: 7px; color: #6b7280;">POC</div>
                        </div>
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #1A237E; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <div style="width: 8px; height: 8px; background: white; border-radius: 50%;"></div>
                            </div>
                            <div style="font-size: 7px; color: #1A237E; font-weight: 600;">Hợp đồng</div>
                        </div>
                    </div>
                    <!-- Contract section -->
                    <div style="padding: 10px;">
                        <div style="text-align: center;">
                            <!-- Create contract button with pulse -->
                            <div style="position: relative; display: inline-block;">
                                <div style="position: absolute; inset: -3px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); border-radius: 6px; animation: tour-contract-btn-pulse 1.5s ease-in-out infinite; opacity: 0.4;"></div>
                                <button style="position: relative; display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); color: white; border: none; border-radius: 4px; font-size: 10px; font-weight: 500;">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><path d="M14 2v6h6M12 18v-6M9 15h6"/></svg>
                                    Tạo hợp đồng
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Info -->
                    <div style="padding: 8px 12px; background: #f0fdf4; border-top: 1px solid #bbf7d0;">
                        <div style="font-size: 9px; color: #166534;">Click nút "Tạo hợp đồng" để tạo hợp đồng mới cho khách hàng</div>
                    </div>
                </div>
                <style>
                    @keyframes tour-contract-btn-pulse {
                        0%, 100% { transform: scale(1); opacity: 0.3; }
                        50% { transform: scale(1.1); opacity: 0.5; }
                    }
                </style>
            </div>
        `
        case 'poc-create-button':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 200px;">
                <div style="width: 340px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Header -->
                    <div style="padding: 10px 12px; background: #f8fafc; border-bottom: 1px solid #e5e7eb;">
                        <div style="font-size: 10px; color: #374151; font-weight: 600;">Quá Trình Bán Hàng #1</div>
                    </div>
                    <!-- Sales Pipeline stages -->
                    <div style="display: grid; grid-template-columns: repeat(4, 1fr); gap: 4px; padding: 10px; border-bottom: 1px solid #e5e7eb;">
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #22c55e; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                            </div>
                            <div style="font-size: 7px; color: #6b7280;">Trao đổi</div>
                        </div>
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #22c55e; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="white" stroke="white" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg>
                            </div>
                            <div style="font-size: 7px; color: #6b7280;">Meeting</div>
                        </div>
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #1A237E; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <div style="width: 8px; height: 8px; background: white; border-radius: 50%;"></div>
                            </div>
                            <div style="font-size: 7px; color: #1A237E; font-weight: 600;">POC</div>
                        </div>
                        <div style="text-align: center;">
                            <div style="width: 20px; height: 20px; border-radius: 50%; background: #e5e7eb; margin: 0 auto 4px; display: flex; align-items: center; justify-content: center;">
                                <div style="width: 8px; height: 8px; background: #9ca3af; border-radius: 50%;"></div>
                            </div>
                            <div style="font-size: 7px; color: #6b7280;">Hợp đồng</div>
                        </div>
                    </div>
                    <!-- POC section -->
                    <div style="padding: 10px;">
                        <div style="text-align: center;">
                            <!-- Create POC button with pulse -->
                            <div style="position: relative; display: inline-block;">
                                <div style="position: absolute; inset: -3px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); border-radius: 6px; animation: tour-poc-btn-pulse 1.5s ease-in-out infinite; opacity: 0.4;"></div>
                                <button style="position: relative; display: flex; align-items: center; gap: 6px; padding: 8px 16px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); color: white; border: none; border-radius: 4px; font-size: 10px; font-weight: 500;">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18"/></svg>
                                    Tạo POC
                                </button>
                            </div>
                        </div>
                    </div>
                    <!-- Info -->
                    <div style="padding: 8px 12px; background: #fef3c7; border-top: 1px solid #fcd34d;">
                        <div style="font-size: 9px; color: #92400e;">Click nút "Tạo POC" để tạo dịch vụ dùng thử cho khách hàng</div>
                    </div>
                </div>
                <style>
                    @keyframes tour-poc-btn-pulse {
                        0%, 100% { transform: scale(1); opacity: 0.3; }
                        50% { transform: scale(1.1); opacity: 0.5; }
                    }
                </style>
            </div>
        `
        case 'lead-detail-modal':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 280px;">
                <div style="display: flex; gap: 12px; align-items: flex-start;">
                    <!-- Left: Table with double-click hint -->
                    <div style="width: 200px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden; flex-shrink: 0;">
                        <!-- Table header -->
                        <div style="display: grid; grid-template-columns: 100px 80px; gap: 4px; padding: 8px 10px; background: #f8fafc; border-bottom: 1px solid #e5e7eb;">
                            <div style="font-size: 8px; color: #6b7280; font-weight: 600;">Tên</div>
                            <div style="font-size: 8px; color: #6b7280; font-weight: 600;">Trạng thái</div>
                        </div>
                        <!-- Row 1 - highlighted with double click -->
                        <div style="position: relative;">
                            <div style="position: absolute; inset: 0; background: linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%); opacity: 0.15; animation: tour-lead-row-pulse 1.2s ease-in-out infinite;"></div>
                            <div style="display: grid; grid-template-columns: 100px 80px; gap: 4px; padding: 8px 10px; border-bottom: 1px solid #f3f4f6; cursor: pointer; position: relative;">
                                <div style="font-size: 9px; color: #374151; font-weight: 500;">Nguyễn Văn A</div>
                                <div style="font-size: 8px; padding: 2px 6px; background: #FEF3C7; color: #B45309; border-radius: 4px; text-align: center;">Warm</div>
                            </div>
                            <!-- Double click cursor -->
                            <div style="position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); animation: tour-cursor-double-click 1.2s ease-in-out infinite;">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1A237E" stroke-width="2">
                                    <path d="M6 9l6 6 6-6"/>
                                </svg>
                            </div>
                        </div>
                        <!-- Row 2 -->
                        <div style="display: grid; grid-template-columns: 100px 80px; gap: 4px; padding: 8px 10px; border-bottom: 1px solid #f3f4f6;">
                            <div style="font-size: 9px; color: #374151;">Trần Thị B</div>
                            <div style="font-size: 8px; padding: 2px 6px; background: #F3F4F6; color: #374151; border-radius: 4px; text-align: center;">Cool</div>
                        </div>
                        <!-- Info -->
                        <div style="padding: 6px 10px; background: #fef3c7;">
                            <div style="font-size: 8px; color: #92400e; display: flex; align-items: center; gap: 4px;">
                                <span style="font-weight: 600;">Double-click</span> để mở chi tiết
                            </div>
                        </div>
                    </div>
                    <!-- Right: Detail modal skeleton -->
                    <div style="width: 240px; background: white; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); overflow: hidden; animation: tour-modal-slide-in 1.2s ease-in-out infinite;">
                        <!-- Modal header -->
                        <div style="padding: 10px 12px; background: #1A237E; display: flex; align-items: center; justify-content: space-between;">
                            <div style="display: flex; align-items: center; gap: 6px;">
                                <div style="font-size: 10px; color: white; font-weight: 600;">Chi tiết Lead</div>
                                <div style="font-size: 8px; padding: 2px 6px; background: #FEF3C7; color: #B45309; border-radius: 4px;">Warm</div>
                            </div>
                            <div style="width: 14px; height: 14px; color: white; opacity: 0.7;">×</div>
                        </div>
                        <!-- Modal body skeleton -->
                        <div style="padding: 12px;">
                            <div style="margin-bottom: 10px;">
                                <div style="font-size: 8px; color: #6b7280; margin-bottom: 4px;">Tên liên hệ</div>
                                <div style="height: 12px; background: #f3f4f6; border-radius: 4px; width: 80%;"></div>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <div style="font-size: 8px; color: #6b7280; margin-bottom: 4px;">Email</div>
                                <div style="height: 12px; background: #f3f4f6; border-radius: 4px; width: 100%;"></div>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <div style="font-size: 8px; color: #6b7280; margin-bottom: 4px;">Công ty</div>
                                <div style="height: 12px; background: #f3f4f6; border-radius: 4px; width: 70%;"></div>
                            </div>
                            <div style="margin-bottom: 10px;">
                                <div style="font-size: 8px; color: #6b7280; margin-bottom: 4px;">Sale phụ trách</div>
                                <div style="height: 12px; background: #dbeafe; border-radius: 4px; width: 60%;"></div>
                            </div>
                            <!-- Tabs skeleton -->
                            <div style="display: flex; gap: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb; margin-top: 8px;">
                                <div style="font-size: 8px; padding: 4px 8px; background: #1A237E; color: white; border-radius: 4px;">ACTION LOG</div>
                                <div style="font-size: 8px; padding: 4px 8px; color: #6b7280;">CHAT</div>
                            </div>
                            <!-- Action log skeleton -->
                            <div style="margin-top: 8px; padding: 8px; background: #f9fafb; border-radius: 4px;">
                                <div style="display: flex; gap: 6px; align-items: flex-start; margin-bottom: 6px;">
                                    <div style="width: 16px; height: 16px; background: #e5e7eb; border-radius: 50%;"></div>
                                    <div style="flex: 1;">
                                        <div style="height: 8px; background: #e5e7eb; border-radius: 2px; width: 60%; margin-bottom: 4px;"></div>
                                        <div style="height: 6px; background: #f3f4f6; border-radius: 2px; width: 80%;"></div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <style>
                    @keyframes tour-lead-row-pulse {
                        0%, 100% { opacity: 0.1; }
                        50% { opacity: 0.25; }
                    }
                    @keyframes tour-cursor-double-click {
                        0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
                        25% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
                        50% { transform: translate(-50%, -50%) scale(1); opacity: 0.8; }
                        75% { transform: translate(-50%, -50%) scale(0.8); opacity: 1; }
                    }
                    @keyframes tour-modal-slide-in {
                        0% { opacity: 0; transform: translateX(20px); }
                        30% { opacity: 1; transform: translateX(0); }
                        100% { opacity: 1; transform: translateX(0); }
                    }
                </style>
            </div>
        `
        case 'lead-status-dropdown':
            return `
            <div class="tour-demo" style="padding: 12px;">
                <div style="width: 220px;">
                    <!-- Select input with skeleton -->
                    <div style="background: white; border: 1px solid #3b82f6; border-radius: 6px; padding: 10px 12px; display: flex; justify-content: space-between; align-items: center; box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);">
                        <div style="height: 14px; background: #e5e7eb; border-radius: 4px; width: 100px;"></div>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                    </div>
                    <!-- Dropdown options -->
                    <div style="background: white; border: 1px solid #e5e7eb; border-radius: 6px; margin-top: 4px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); overflow: hidden; animation: tour-modal-appear 2s ease-in-out infinite;">
                        <!-- Warm Lead -->
                        <div style="padding: 10px 12px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #f3f4f6; background: #fff7ed;">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f97316" stroke-width="2"><path d="M12 2c.5 0 1 .2 1.4.5L17 5c.8.6 1 1.5 1 2.3v5.4c0 .8-.4 1.6-1 2.2l-3.6 3.3c-.4.4-.9.6-1.4.6s-1-.2-1.4-.6L7 15c-.6-.6-1-1.4-1-2.2V7.3c0-.8.2-1.7 1-2.3l3.6-2.5c.4-.3.9-.5 1.4-.5z"/></svg>
                            <span style="font-size: 12px; color: #ea580c; font-weight: 500;">Warm Lead (MQL)</span>
                        </div>
                        <!-- Cool Lead -->
                        <div style="padding: 10px 12px; display: flex; align-items: center; gap: 8px; border-bottom: 1px solid #f3f4f6;">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#3b82f6" stroke-width="2"><path d="M12 2L4.5 20.3l.7.7L12 18l6.8 3 .7-.7L12 2z"/></svg>
                            <span style="font-size: 12px; color: #374151;">Cool Lead</span>
                        </div>
                        <!-- Unqualified -->
                        <div style="padding: 10px 12px; display: flex; align-items: center; gap: 8px;">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M4.93 4.93l14.14 14.14"/></svg>
                            <span style="font-size: 12px; color: #374151;">Unqualified</span>
                        </div>
                    </div>
                </div>
            </div>
        `
        case 'lead-filters-overview':
            return `
            <div class="tour-demo" style="padding: 12px;">
                <div style="width: 400px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); padding: 12px;">
                    <!-- Row 1: Search, Date, Status -->
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                        <!-- Search -->
                        <div style="position: relative;">
                            <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px; display: flex; align-items: center; gap: 6px;">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                                <div style="font-size: 9px; color: #9ca3af;">Tìm kiếm...</div>
                            </div>
                            <div style="position: absolute; -top-1px; left: 4px; font-size: 7px; color: #6b7280; background: white; padding: 0 2px;">Tìm kiếm</div>
                        </div>
                        <!-- Date -->
                        <div style="position: relative;">
                            <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between;">
                                <div style="font-size: 9px; color: #374151;">7 ngày qua</div>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                            </div>
                            <div style="position: absolute; -top-1px; left: 4px; font-size: 7px; color: #6b7280; background: white; padding: 0 2px;">Thời gian</div>
                        </div>
                        <!-- Status - highlighted -->
                        <div style="position: relative; animation: tour-filter-pulse 1.5s ease-in-out infinite;">
                            <div style="background: #fef3c7; border: 1px solid #f59e0b; border-radius: 4px; padding: 6px 8px; display: flex; align-items: center; justify-content: space-between;">
                                <div style="font-size: 9px; color: #b45309; font-weight: 500;">Warm Lead</div>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#b45309" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                            </div>
                            <div style="position: absolute; -top-1px; left: 4px; font-size: 7px; color: #b45309; background: white; padding: 0 2px;">Trạng thái</div>
                        </div>
                    </div>
                    <!-- Row 2: Assignment, Website, Channel -->
                    <div style="display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 8px; margin-bottom: 8px;">
                        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px;">
                            <div style="font-size: 9px; color: #9ca3af;">Phân công</div>
                        </div>
                        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px;">
                            <div style="font-size: 9px; color: #9ca3af;">Website</div>
                        </div>
                        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px;">
                            <div style="font-size: 9px; color: #9ca3af;">Kênh</div>
                        </div>
                    </div>
                    <!-- Row 3: Product, Assignee -->
                    <div style="display: grid; grid-template-columns: 1fr 2fr; gap: 8px;">
                        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px;">
                            <div style="font-size: 9px; color: #9ca3af;">Nhóm SP</div>
                        </div>
                        <div style="background: #dbeafe; border: 1px solid #60a5fa; border-radius: 4px; padding: 6px 8px; display: flex; align-items: center; gap: 4px;">
                            <div style="width: 16px; height: 16px; background: #3b82f6; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                                <span style="font-size: 7px; color: white; font-weight: 600;">NV</span>
                            </div>
                            <div style="font-size: 9px; color: #1e40af;">Nguyễn Văn A</div>
                        </div>
                    </div>
                    <!-- Info -->
                    <div style="margin-top: 10px; padding: 8px; background: #f0fdf4; border-radius: 4px; border-left: 3px solid #22c55e;">
                        <div style="font-size: 8px; color: #166534;">Các bộ lọc tự động áp dụng khi thay đổi. Kết hợp nhiều filter để tìm kiếm chính xác.</div>
                    </div>
                </div>
                <style>
                    @keyframes tour-filter-pulse {
                        0%, 100% { transform: scale(1); }
                        50% { transform: scale(1.02); box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.3); }
                    }
                </style>
            </div>
        `
        case 'classify-lead-modal':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 180px;">
                <div style="width: 280px; background: white; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); overflow: hidden; animation: tour-modal-appear 2.5s ease-in-out infinite;">
                    <!-- Header -->
                    <div style="padding: 12px 14px; border-bottom: 1px solid #e5e7eb;">
                        <div style="font-size: 12px; font-weight: 600; color: #1f2937;">Phân loại Lead</div>
                    </div>
                    <!-- Info banner -->
                    <div style="margin: 10px; padding: 8px 10px; background: #FEF3C7; border-radius: 6px; border-left: 3px solid #F59E0B;">
                        <div style="font-size: 8px; color: #92400E;">Phân loại lead dựa trên tiêu chí BANT</div>
                    </div>
                    <!-- Lead type cards -->
                    <div style="padding: 0 10px 10px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;">
                        <div style="padding: 8px; background: #f0fdf4; border-radius: 6px; border-left: 3px solid #22c55e;">
                            <div style="font-size: 9px; font-weight: 600; color: #166534; margin-bottom: 4px;">Warm Lead</div>
                            <div style="font-size: 7px; color: #15803d;">Tiềm năng cao</div>
                        </div>
                        <div style="padding: 8px; background: #fef3c7; border-radius: 6px; border-left: 3px solid #f59e0b; animation: tour-filter-pulse 1s ease-in-out infinite;">
                            <div style="font-size: 9px; font-weight: 600; color: #b45309; margin-bottom: 4px;">Cool Lead</div>
                            <div style="font-size: 7px; color: #a16207;">Tiềm năng thấp</div>
                        </div>
                        <div style="padding: 8px; background: #fef2f2; border-radius: 6px; border-left: 3px solid #ef4444;">
                            <div style="font-size: 9px; font-weight: 600; color: #dc2626; margin-bottom: 4px;">Unqualified</div>
                            <div style="font-size: 7px; color: #b91c1c;">Không đủ ĐK</div>
                        </div>
                    </div>
                    <!-- Select -->
                    <div style="padding: 0 10px 10px;">
                        <div style="font-size: 8px; color: #6b7280; margin-bottom: 4px;">Phân loại Lead</div>
                        <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 10px; display: flex; justify-content: space-between; align-items: center;">
                            <span style="font-size: 9px; color: #9ca3af;">Chọn loại lead</span>
                            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                        </div>
                    </div>
                    <!-- Footer -->
                    <div style="padding: 10px 14px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 8px;">
                        <button style="padding: 6px 16px; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 9px; color: #6b7280; background: white;">Huỷ</button>
                        <button style="padding: 6px 16px; border: none; border-radius: 4px; font-size: 9px; color: white; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%);">Lưu</button>
                    </div>
                </div>
            </div>
        `
        case 'add-contact-modal':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 160px;">
                <div style="width: 240px; background: white; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); overflow: hidden; animation: tour-modal-appear 2.5s ease-in-out infinite;">
                    <!-- Header -->
                    <div style="padding: 10px 12px; background: linear-gradient(135deg, #059669 0%, #10b981 100%); display: flex; align-items: center; gap: 8px;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2">
                            <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z"/>
                        </svg>
                        <div style="font-size: 11px; color: white; font-weight: 600;">Thêm liên hệ mới</div>
                    </div>
                    <!-- Body -->
                    <div style="padding: 12px;">
                        <div style="margin-bottom: 10px;">
                            <div style="font-size: 8px; color: #6b7280; margin-bottom: 3px;">Tên liên hệ *</div>
                            <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px; font-size: 9px; color: #374151;">Nguyễn Văn A</div>
                        </div>
                        <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px; margin-bottom: 10px;">
                            <div>
                                <div style="font-size: 8px; color: #6b7280; margin-bottom: 3px;">Email</div>
                                <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px; font-size: 9px; color: #9ca3af;">email@...</div>
                            </div>
                            <div>
                                <div style="font-size: 8px; color: #6b7280; margin-bottom: 3px;">Số điện thoại</div>
                                <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px; font-size: 9px; color: #9ca3af;">0xxx...</div>
                            </div>
                        </div>
                        <div>
                            <div style="font-size: 8px; color: #6b7280; margin-bottom: 3px;">Vai trò</div>
                            <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 6px 8px; display: flex; justify-content: space-between;">
                                <span style="font-size: 9px; color: #9ca3af;">Chọn vai trò...</span>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" stroke-width="2"><path d="M6 9l6 6 6-6"/></svg>
                            </div>
                        </div>
                    </div>
                    <!-- Footer -->
                    <div style="padding: 10px 12px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 8px;">
                        <button style="padding: 5px 12px; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 9px; color: #6b7280; background: white;">Huỷ</button>
                        <button style="padding: 5px 12px; border: none; border-radius: 4px; font-size: 9px; color: white; background: linear-gradient(135deg, #059669 0%, #10b981 100%);">Lưu</button>
                    </div>
                </div>
            </div>
        `
        case 'add-note-modal':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 140px;">
                <div style="width: 220px; background: white; border-radius: 8px; box-shadow: 0 4px 20px rgba(0,0,0,0.15); overflow: hidden; animation: tour-modal-appear 2.5s ease-in-out infinite;">
                    <!-- Header -->
                    <div style="padding: 10px 12px; border-bottom: 1px solid #e5e7eb; display: flex; align-items: center; gap: 8px;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" stroke-width="2">
                            <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
                            <path d="M14 2v6h6M16 13H8M16 17H8M10 9H8"/>
                        </svg>
                        <div style="font-size: 11px; color: #1f2937; font-weight: 600;">Thêm ghi chú</div>
                    </div>
                    <!-- Body -->
                    <div style="padding: 12px;">
                        <div style="margin-bottom: 10px;">
                            <div style="font-size: 8px; color: #6b7280; margin-bottom: 3px;">Nội dung ghi chú *</div>
                            <div style="background: #f9fafb; border: 1px solid #e5e7eb; border-radius: 4px; padding: 8px; min-height: 50px;">
                                <div style="font-size: 9px; color: #9ca3af;">Nhập nội dung ghi chú...</div>
                            </div>
                        </div>
                        <div style="display: flex; align-items: center; gap: 6px;">
                            <div style="width: 14px; height: 14px; border: 1px solid #d1d5db; border-radius: 3px; background: white;"></div>
                            <span style="font-size: 8px; color: #6b7280;">Ghim ghi chú này</span>
                        </div>
                    </div>
                    <!-- Footer -->
                    <div style="padding: 10px 12px; border-top: 1px solid #e5e7eb; display: flex; justify-content: flex-end; gap: 8px;">
                        <button style="padding: 5px 12px; border: 1px solid #e5e7eb; border-radius: 4px; font-size: 9px; color: #6b7280; background: white;">Huỷ</button>
                        <button style="padding: 5px 12px; border: none; border-radius: 4px; font-size: 9px; color: white; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%);">Lưu</button>
                    </div>
                </div>
            </div>
        `

        // Merge Invoices Demos
        case 'invoice-selection-summary':
            return `
            <div class="tour-demo" style="padding: 12px;">
                <div style="width: 340px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Selection summary bar -->
                    <div style="padding: 10px 14px; background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%); border-bottom: 2px solid #3B82F6; display: flex; align-items: center; justify-content: space-between;">
                        <div style="display: flex; align-items: center; gap: 12px;">
                            <!-- Selection count -->
                            <div style="display: flex; align-items: center; gap: 6px;">
                                <div style="width: 22px; height: 22px; background: #3B82F6; border-radius: 50%; display: flex; align-items: center; justify-content: center;">
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="3"><path d="M20 6L9 17l-5-5"/></svg>
                                </div>
                                <span style="font-size: 11px; color: #1E40AF; font-weight: 600;">2 hóa đơn đã chọn</span>
                            </div>
                            <!-- Total amount with animation -->
                            <div style="display: flex; align-items: center; gap: 4px; padding: 4px 10px; background: white; border-radius: 4px; animation: tour-amount-pulse 1.5s ease-in-out infinite;">
                                <span style="font-size: 9px; color: #6B7280;">Tổng:</span>
                                <span style="font-size: 11px; color: #1A237E; font-weight: 700; font-variant-numeric: tabular-nums;">47.850.585 ₫</span>
                            </div>
                        </div>
                        <!-- Merge button -->
                        <div style="position: relative;">
                            <div style="position: absolute; inset: -3px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); border-radius: 6px; animation: tour-btn-pulse 1.5s ease-in-out infinite; opacity: 0.4;"></div>
                            <button style="position: relative; display: flex; align-items: center; gap: 5px; padding: 6px 12px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); color: white; border: none; border-radius: 4px; font-size: 10px; font-weight: 500;">
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                                    <polyline points="7.5 4.21 12 6.81 16.5 4.21"/>
                                    <polyline points="7.5 19.79 7.5 14.6 3 12"/>
                                    <polyline points="21 12 16.5 14.6 16.5 19.79"/>
                                    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                                    <line x1="12" y1="22.08" x2="12" y2="12"/>
                                </svg>
                                Gộp Hóa Đơn
                            </button>
                        </div>
                    </div>
                    <!-- Invoice rows preview -->
                    <div style="padding: 0;">
                        <!-- Selected row 1 -->
                        <div style="display: grid; grid-template-columns: 60px 1fr 90px; gap: 8px; padding: 8px 12px; background: #F0FDF4; border-left: 3px solid #22C55E;">
                            <div style="font-size: 10px; color: #15803D; font-weight: 600;">#107860</div>
                            <div style="font-size: 9px; color: #166534;">VPS Hosting + Domain</div>
                            <div style="font-size: 10px; color: #166534; text-align: right; font-weight: 600;">23.925.292 ₫</div>
                        </div>
                        <!-- Selected row 2 -->
                        <div style="display: grid; grid-template-columns: 60px 1fr 90px; gap: 8px; padding: 8px 12px; background: #F0FDF4; border-left: 3px solid #22C55E;">
                            <div style="font-size: 10px; color: #15803D; font-weight: 600;">#107852</div>
                            <div style="font-size: 9px; color: #166534;">Email Service</div>
                            <div style="font-size: 10px; color: #166534; text-align: right; font-weight: 600;">23.925.293 ₫</div>
                        </div>
                    </div>
                </div>
                <style>
                    @keyframes tour-amount-pulse {
                        0%, 100% { transform: scale(1); box-shadow: 0 0 0 0 rgba(26, 35, 126, 0.3); }
                        50% { transform: scale(1.05); box-shadow: 0 0 0 4px rgba(26, 35, 126, 0.1); }
                    }
                    @keyframes tour-btn-pulse {
                        0%, 100% { transform: scale(1); opacity: 0.3; }
                        50% { transform: scale(1.1); opacity: 0.5; }
                    }
                </style>
            </div>
        `

        case 'merge-button-click':
            return `
            <div class="tour-demo" style="padding: 12px;">
                <div style="width: 280px; background: white; border-radius: 8px; box-shadow: 0 2px 8px rgba(0,0,0,0.1); overflow: hidden;">
                    <!-- Button with click animation -->
                    <div style="padding: 16px; text-align: center;">
                        <div style="position: relative; display: inline-block;">
                            <!-- Pulse effect -->
                            <div style="position: absolute; inset: -4px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); border-radius: 8px; animation: tour-merge-btn-pulse 1.2s ease-in-out infinite; opacity: 0.4;"></div>
                            <!-- Button -->
                            <button style="position: relative; display: flex; align-items: center; gap: 6px; padding: 10px 20px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); color: white; border: none; border-radius: 6px; font-size: 12px; font-weight: 600; animation: tour-btn-click 1.2s ease-in-out infinite;">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                                    <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/>
                                </svg>
                                Gộp Hóa Đơn
                            </button>
                        </div>
                    </div>
                    <!-- Info -->
                    <div style="padding: 10px 14px; background: #EFF6FF; border-top: 1px solid #BFDBFE;">
                        <div style="font-size: 9px; color: #1E40AF; text-align: center;">
                            Click nút này để mở modal xác nhận gộp hóa đơn
                        </div>
                    </div>
                </div>
                <!-- Cursor pointing -->
                <div style="display: flex; align-items: center; justify-content: center; gap: 6px; margin-top: 10px; animation: tour-cursor-bounce 1s ease-in-out infinite;">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="#374151" stroke="#374151" stroke-width="1">
                        <path d="M4 4l7.07 17 2.51-7.39L21 11.07z"/>
                    </svg>
                    <span style="font-size: 10px; color: #6b7280; font-weight: 500;">Click vào nút "Gộp Hóa Đơn"</span>
                </div>
                <style>
                    @keyframes tour-merge-btn-pulse {
                        0%, 100% { transform: scale(1); opacity: 0.3; }
                        50% { transform: scale(1.15); opacity: 0.6; }
                    }
                    @keyframes tour-btn-click {
                        0%, 100% { transform: scale(1); }
                        10%, 30% { transform: scale(0.95); }
                        20%, 40% { transform: scale(1); }
                    }
                    @keyframes tour-cursor-bounce {
                        0%, 100% { transform: translateY(0); }
                        50% { transform: translateY(-4px); }
                    }
                </style>
            </div>
        `

        case 'merge-preview-modal':
            return `
            <div class="tour-demo" style="padding: 12px; min-height: 280px;">
                <div style="width: 380px; background: white; border-radius: 10px; box-shadow: 0 8px 32px rgba(0,0,0,0.2); overflow: hidden; animation: tour-modal-appear 2s ease-in-out infinite;">
                    <!-- Modal header -->
                    <div style="padding: 14px 16px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); display: flex; align-items: center; justify-content: space-between;">
                        <div style="font-size: 13px; color: white; font-weight: 600;">Xác Nhận Gộp Hóa Đơn</div>
                        <div style="width: 18px; height: 18px; color: white; opacity: 0.8;">×</div>
                    </div>
                    <!-- Description -->
                    <div style="padding: 12px 16px; background: #FEF3C7; border-bottom: 1px solid #FCD34D;">
                        <div style="font-size: 9px; color: #92400E;">
                            Bạn có chắc chắn muốn gộp <strong>2 hóa đơn (107860, 107852)</strong> thành một hóa đơn duy nhất? Hành động này không thể hoàn tác.
                        </div>
                    </div>
                    <!-- Invoice preview table -->
                    <div style="padding: 12px 16px;">
                        <div style="border: 1px solid #E5E7EB; border-radius: 6px; overflow: hidden;">
                            <!-- Table header -->
                            <div style="display: grid; grid-template-columns: 120px 70px 80px; gap: 6px; padding: 8px 10px; background: #F8FAFC; border-bottom: 1px solid #E5E7EB;">
                                <div style="font-size: 8px; color: #6B7280; font-weight: 600;">DỊCH VỤ</div>
                                <div style="font-size: 8px; color: #6B7280; font-weight: 600;">KỲ ÁP DỤNG</div>
                                <div style="font-size: 8px; color: #6B7280; font-weight: 600; text-align: right;">TỔNG TIỀN</div>
                            </div>
                            <!-- Invoice #107860 header -->
                            <div style="padding: 6px 10px; background: #F1F5F9; border-bottom: 1px solid #E5E7EB;">
                                <div style="font-size: 8px; color: #475569; font-weight: 500;">#107860 - 01/01/2026 đến 31/01/2026</div>
                            </div>
                            <!-- Item 1 -->
                            <div style="display: grid; grid-template-columns: 120px 70px 80px; gap: 6px; padding: 6px 10px; border-bottom: 1px solid #F3F4F6;">
                                <div style="font-size: 9px; color: #374151;">VPS Hosting</div>
                                <div style="font-size: 8px; color: #6B7280;">01-31/01</div>
                                <div style="font-size: 9px; color: #374151; text-align: right; font-weight: 500;">20.000.000 ₫</div>
                            </div>
                            <!-- Item 2 -->
                            <div style="display: grid; grid-template-columns: 120px 70px 80px; gap: 6px; padding: 6px 10px; border-bottom: 2px solid #CBD5E1;">
                                <div style="font-size: 9px; color: #374151;">Domain .vn</div>
                                <div style="font-size: 8px; color: #6B7280;">01-31/01</div>
                                <div style="font-size: 9px; color: #374151; text-align: right; font-weight: 500;">3.925.292 ₫</div>
                            </div>
                            <!-- Invoice #107852 header -->
                            <div style="padding: 6px 10px; background: #F1F5F9; border-bottom: 1px solid #E5E7EB;">
                                <div style="font-size: 8px; color: #475569; font-weight: 500;">#107852 - 01/02/2026 đến 28/02/2026</div>
                            </div>
                            <!-- Item 3 -->
                            <div style="display: grid; grid-template-columns: 120px 70px 80px; gap: 6px; padding: 6px 10px;">
                                <div style="font-size: 9px; color: #374151;">Email Service</div>
                                <div style="font-size: 8px; color: #6B7280;">01-28/02</div>
                                <div style="font-size: 9px; color: #374151; text-align: right; font-weight: 500;">23.925.293 ₫</div>
                            </div>
                            <!-- Total footer -->
                            <div style="padding: 10px; background: #EEF2FF; border-top: 2px solid #818CF8; display: flex; justify-content: space-between; align-items: center;">
                                <span style="font-size: 10px; color: #3730A3; font-weight: 600;">Tổng Cộng</span>
                                <span style="font-size: 12px; color: #3730A3; font-weight: 700; font-variant-numeric: tabular-nums;">47.850.585 ₫</span>
                            </div>
                        </div>
                    </div>
                    <!-- Modal footer -->
                    <div style="padding: 12px 16px; background: #F9FAFB; border-top: 1px solid #E5E7EB; display: flex; justify-content: flex-end; gap: 10px;">
                        <button style="padding: 7px 16px; border: 1px solid #D1D5DB; border-radius: 6px; font-size: 10px; color: #6B7280; background: white; font-weight: 500;">Hủy</button>
                        <div style="position: relative;">
                            <div style="position: absolute; inset: -2px; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); border-radius: 8px; animation: tour-confirm-pulse 1.5s ease-in-out infinite; opacity: 0.4;"></div>
                            <button style="position: relative; padding: 7px 16px; border: none; border-radius: 6px; font-size: 10px; color: white; background: linear-gradient(135deg, #1A237E 0%, #3949AB 100%); font-weight: 600;">Xác Nhận</button>
                        </div>
                    </div>
                </div>
                <style>
                    @keyframes tour-modal-appear {
                        0% { opacity: 0; transform: scale(0.95); }
                        20% { opacity: 1; transform: scale(1); }
                        100% { opacity: 1; transform: scale(1); }
                    }
                    @keyframes tour-confirm-pulse {
                        0%, 100% { transform: scale(1); opacity: 0.3; }
                        50% { transform: scale(1.08); opacity: 0.6; }
                    }
                </style>
            </div>
        `

        default:
            return ''
    }
}
